import React, { useEffect, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Row, Col, Table, Spin, Select, Card, Button, DatePicker, Space, Modal, Tabs, Switch } from 'antd'
import { getActivityLog } from '../../store/action/patient.action'
import { CloseCircleOutlined, DownloadOutlined, FilterOutlined, EyeOutlined } from '@ant-design/icons'
import '../../css/patient.scss'
import { constants } from '../../constants'
import { formatDateTimeET } from '../../util/datetime'
import ApolloClientService from '../../services/apolloclient.service'
import {
  getEventSource,
  getEventName,
  generateDescription,
  getUniqueEventNames,
  getUniqueSources,
  filterActivities,
  exportToCSV,
  parseNewData,
  formatTimeLogged,
  enhanceActivitiesWithNames,
  generateEnhancedDescription,
  exportRawActivityLogs
} from '../../util/activityLog.util'

const apolloService = new ApolloClientService()

const { Option } = Select
const { RangePicker } = DatePicker
const ActivityLog = (props) => {
  const [filters, setFilters] = useState({
    sources: [],
    eventNames: [],
    fromDate: null,
    toDate: null,
    dateRange: null,
    showNotifications: false
  })
  const [debugMode, setDebugMode] = useState(false)
  const [debugOutput, setDebugOutput] = useState('')
  const [enhancedActivities, setEnhancedActivities] = useState([])
  const [isEnhancing, setIsEnhancing] = useState(false)
  const [contentModalVisible, setContentModalVisible] = useState(false)
  const [contentModalTitle, setContentModalTitle] = useState('')
  const [contentModalHtml, setContentModalHtml] = useState([])
  const [contentLoading, setContentLoading] = useState(false)

  const isConsentOrTncRow = (record) => {
    const collection = record.collection_name
    const method = record.method
    return (
      (collection === 'termsandconditions' && method === 'acceptTermsAndConditions') ||
      (collection === 'patientconsents' && method === 'addConsentForRegisteredPatient')
    )
  }

  const handleViewContent = async (record) => {
    const collection = record.collection_name
    const method = record.method

    setContentLoading(true)
    setContentModalVisible(true)

    try {
      if (collection === 'termsandconditions' && method === 'acceptTermsAndConditions') {
        const newData = parseNewData(record.newdata)
        const version = newData?.version
        setContentModalTitle(`Terms & Conditions${version ? ` - Version ${version}` : ''}`)

        if (version) {
          const res = await apolloService.getTncByVersion(version)
          if (res.status === 'success' && res.tnc?.content) {
            setContentModalHtml([{ title: `Version ${version}`, content: res.tnc.content }])
          } else {
            setContentModalHtml([{ title: 'Error', content: '<p>Unable to load T&C content. ' + (res.message || '') + '</p>' }])
          }
        } else {
          setContentModalHtml([{ title: 'Error', content: '<p>Version information not available for this entry.</p>' }])
        }
      } else if (collection === 'patientconsents' && method === 'addConsentForRegisteredPatient') {
        setContentModalTitle('Signed Consent Forms')
        const patientid = record.patientid
        if (patientid) {
          const res = await apolloService.getPatientConsentContent(patientid)
          if (res.status === 'success' && res.forms?.length > 0) {
            setContentModalHtml(res.forms.map(f => ({
              title: f.title || 'Consent Form',
              content: f.content || '<p>Content not available for this form.</p>'
            })))
          } else {
            setContentModalHtml([{ title: 'Info', content: '<p>No consent form content available.</p>' }])
          }
        } else {
          setContentModalHtml([{ title: 'Error', content: '<p>Patient ID not available for this entry.</p>' }])
        }
      }
    } catch (err) {
      console.error('Error loading content:', err)
      setContentModalHtml([{ title: 'Error', content: '<p>Failed to load content. Please try again.</p>' }])
    }

    setContentLoading(false)
  }

  const columns = [
    {
      title: 'Time',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: '180px',
      defaultSortOrder: 'descend',
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      render: (date) => {
        return date ? formatDateTimeET(date) : 'NA'
      },
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      render: (text, record) => getEventSource(record),
    },
    {
      title: 'Event Name',
      dataIndex: 'eventName',
      key: 'eventName',
      render: (text, record) => getEventName(record),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: false,
      render: (text, record) => {
        const description = generateEnhancedDescription(record)
        const short = description.length > 300 ? description.slice(0, 300) + '...' : description
        return (
          <div
            style={{
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            }}
          >
            {short}
            {isConsentOrTncRow(record) && (
              <>
                {' '}
                <a
                  onClick={(e) => {
                    e.stopPropagation()
                    handleViewContent(record)
                  }}
                  style={{ marginLeft: 8, whiteSpace: 'nowrap' }}
                >
                  <EyeOutlined /> View Content
                </a>
              </>
            )}
          </div>
        )
      },
    },
  ]

  const uniqueEventNames = useMemo(() => {
    return getUniqueEventNames(props.activitylogs || [])
  }, [props.activitylogs])

  const uniqueSources = useMemo(() => {
    return getUniqueSources(props.activitylogs || [])
  }, [props.activitylogs])

  // Enhance activities with exercise names when data changes
  useEffect(() => {
    const enhanceData = async () => {
      if (props.activitylogs && props.activitylogs.length > 0) {
        setIsEnhancing(true)
        try {
          const enhanced = await enhanceActivitiesWithNames(props.activitylogs)
          setEnhancedActivities(enhanced)
        } catch (error) {
          console.error('Error enhancing activities:', error)
          setEnhancedActivities(props.activitylogs || [])
        }
        setIsEnhancing(false)
      }
    }
    
    enhanceData()
  }, [props.activitylogs])

  const filteredActivities = useMemo(() => {
    const activities = enhancedActivities.length > 0 ? enhancedActivities : (props.activitylogs || [])
    const filtered = filterActivities(activities, filters)
    
    // Generate debug output
    if (debugMode && activities.length > 0) {
      let debugText = `=== ACTIVITY LOG DEBUG OUTPUT ===\n`
      debugText += `Total activities: ${activities.length}\n`
      debugText += `Filtered activities: ${filtered.length}\n\n`
      
      debugText += `=== FIRST 10 RAW ACTIVITIES ===\n`
      activities.slice(0, 10).forEach((activity, index) => {
        debugText += `\n--- Activity ${index + 1} ---\n`
        debugText += `Collection: ${activity.collection_name}\n`
        debugText += `Action: ${activity.action}\n`
        debugText += `Application: ${activity.application}\n`
        debugText += `Actiontype: ${activity.actiontype}\n`
        debugText += `Method: ${activity.method}\n`
        debugText += `Event Name: ${getEventName(activity)}\n`
        debugText += `Source: ${getEventSource(activity)}\n`
        debugText += `Description: ${generateDescription(activity)}\n`
        
        // Special debug info for Patient Notes
        if (activity.collection_name === 'patientnotes') {
          debugText += `\n  PATIENT NOTE DETAILS:\n`
          debugText += `  Raw newdata: ${activity.newdata}\n`
          const parsedData = parseNewData(activity.newdata)
          debugText += `  Parsed newdata: ${JSON.stringify(parsedData, null, 2)}\n`
          if (parsedData && parsedData.timelogged) {
            debugText += `  Timelogged value: ${parsedData.timelogged} (type: ${typeof parsedData.timelogged})\n`
            debugText += `  Formatted time: ${formatTimeLogged(parsedData.timelogged)}\n`
          }
          if (parsedData && parsedData.note) {
            debugText += `  Note content: ${parsedData.note}\n`
          }
        }
      })
      
      debugText += `\n=== EXERCISE/QUESTIONNAIRE/MEASUREMENT ANALYSIS ===\n`
      
      // Exercise analysis
      const exercises = activities.filter(activity => 
        activity.method === 'updateExercise' && activity.collection_name === 'patientexerciseschedule')
      debugText += `Total Exercises found: ${exercises.length}\n`
      exercises.slice(0, 2).forEach((activity, index) => {
        debugText += `\n--- Exercise ${index + 1} ---\n`
        debugText += `Raw newdata: ${activity.newdata}\n`
        const parsedData = parseNewData(activity.newdata)
        debugText += `Parsed newdata: ${JSON.stringify(parsedData, null, 2)}\n`
        debugText += `Current Description: ${generateDescription(activity)}\n`
      })
      
      // Questionnaire analysis
      const questionnaires = activities.filter(activity => 
        activity.method === 'updateFeedback' && activity.collection_name === 'patientfeedschedule')
      debugText += `\nTotal Questionnaires found: ${questionnaires.length}\n`
      questionnaires.slice(0, 2).forEach((activity, index) => {
        debugText += `\n--- Questionnaire ${index + 1} ---\n`
        debugText += `Raw newdata: ${activity.newdata}\n`
        const parsedData = parseNewData(activity.newdata)
        debugText += `Parsed newdata: ${JSON.stringify(parsedData, null, 2)}\n`
        debugText += `Current Description: ${generateDescription(activity)}\n`
      })
      
      // Measurement analysis
      const measurements = activities.filter(activity => activity.collection_name === 'devicemeasurement')
      debugText += `\nTotal Measurements found: ${measurements.length}\n`
      measurements.slice(0, 2).forEach((activity, index) => {
        debugText += `\n--- Measurement ${index + 1} ---\n`
        debugText += `Raw newdata: ${activity.newdata}\n`
        const parsedData = parseNewData(activity.newdata)
        debugText += `Parsed newdata: ${JSON.stringify(parsedData, null, 2)}\n`
        debugText += `Current Description: ${generateDescription(activity)}\n`
      })
      
      debugText += `\n=== PATIENT NOTES ANALYSIS ===\n`
      const patientNotes = activities.filter(activity => activity.collection_name === 'patientnotes')
      debugText += `Total Patient Notes found: ${patientNotes.length}\n`
      patientNotes.slice(0, 3).forEach((activity, index) => {
        debugText += `\n--- Patient Note ${index + 1} ---\n`
        debugText += `Method: ${activity.method}\n`
        debugText += `Application: ${activity.application}\n`
        debugText += `Actiontype: ${activity.actiontype}\n`
        debugText += `Raw newdata: ${activity.newdata}\n`
        const parsedData = parseNewData(activity.newdata)
        debugText += `Parsed newdata: ${JSON.stringify(parsedData, null, 2)}\n`
        if (parsedData && parsedData.time_logged) {
          debugText += `Time_logged: ${parsedData.time_logged} (${typeof parsedData.time_logged})\n`
          debugText += `Formatted: ${formatTimeLogged(parsedData.time_logged)}\n`
        }
        if (parsedData && parsedData.notes) {
          debugText += `Notes: ${parsedData.notes.trim()}\n`
        }
        debugText += `Current Description: ${generateDescription(activity)}\n`
      })
      
      debugText += `\n=== UNIQUE EVENT NAMES ===\n`
      uniqueEventNames.forEach(name => {
        debugText += `- ${name}\n`
      })
      
      debugText += `\n=== UNIQUE SOURCES ===\n`
      uniqueSources.forEach(source => {
        debugText += `- ${source}\n`
      })
      
      debugText += `\n=== CURRENT FILTERS ===\n`
      debugText += `Sources: [${filters.sources.join(', ')}]\n`
      debugText += `Event Names: [${filters.eventNames.join(', ')}]\n`
      debugText += `From Date: ${filters.fromDate || 'None'}\n`
      debugText += `To Date: ${filters.toDate || 'None'}\n`
      
      setDebugOutput(debugText)
    }
    
    return filtered
  }, [enhancedActivities, props.activitylogs, filters, debugMode, uniqueEventNames, uniqueSources])

  const handleTimeChange = (value) => {
    setFilters(prev => ({ ...prev, dateRange: value, fromDate: null, toDate: null }))
    props.getActivityLog({ patientid: props.patient?._id, fromDate: value })
  }

  const handleSourceChange = (values) => {
    setFilters(prev => ({ ...prev, sources: values }))
  }

  const handleEventNameChange = (values) => {
    setFilters(prev => ({ ...prev, eventNames: values }))
  }

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      setFilters(prev => ({
        ...prev,
        fromDate: dates[0],
        toDate: dates[1],
        dateRange: null
      }))
    } else {
      setFilters(prev => ({
        ...prev,
        fromDate: null,
        toDate: null
      }))
    }
  }

  const handleClearFilters = () => {
    setFilters({
      sources: [],
      eventNames: [],
      fromDate: null,
      toDate: null,
      dateRange: null,
      showNotifications: false
    })
  }

  const handleExportCSV = () => {
    const success = exportToCSV(filteredActivities)
    if (!success) {
      console.log('No data to export')
    }
  }

  const handleCopyDebug = () => {
    navigator.clipboard.writeText(debugOutput).then(() => {
      console.log('Debug output copied to clipboard')
    }).catch(err => {
      console.error('Failed to copy debug output:', err)
    })
  }

  // Internal Export handler for SUPER_ADMIN users only
  const handleInternalExport = () => {
    // Use all activity logs data, not just the filtered/paginated view
    const allData = props.activitylogs || []
    console.log('Internal Export - Total records from API:', allData.length)
    console.log('Internal Export - Filters applied:', filters)
    const success = exportRawActivityLogs(allData, filters)
    if (!success) {
      console.log('No data to export')
    }
  }

  // Check if current user is SUPER_ADMIN
  const isSuperAdmin = props.user?.role?.toString() === constants.ROLE.SUPER_ADMIN
  return (
    <>
      <Card bordered={false}>
        <div className="filterRow">
          <Row gutter={[16, 16]} style={{ marginBottom: 10, marginTop: 15 }}>
            <Col xs={24} sm={12} md={6}>
              <Select
                className='dashboard-select'
                onChange={handleTimeChange}
                placeholder='Quick date range'
                value={filters.dateRange}
                allowClear
                style={{ width: '100%' }}
              >
                <Option value={'1d'}>1 day</Option>
                <Option value={'3d'}>3 days</Option>
                <Option value={'7d'}>1 week</Option>
                <Option value={'14d'}>2 weeks</Option>
                <Option value={'28d'}>4 weeks</Option>
                <Option value={'1m'}>1 month</Option>
                <Option value={'2m'}>2 months</Option>
                <Option value={'3m'}>3 months</Option>
                <Option value={'6m'}>6 months</Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <RangePicker
                style={{ width: '100%' }}
                onChange={handleDateRangeChange}
                value={filters.fromDate && filters.toDate ? [filters.fromDate, filters.toDate] : null}
                placeholder={['From Date', 'To Date']}
              />
            </Col>
            <Col xs={24} sm={12} md={5}>
              <Select
                mode="multiple"
                placeholder="Filter by Source"
                value={filters.sources}
                onChange={handleSourceChange}
                style={{ width: '100%' }}
                allowClear
              >
                {uniqueSources.map(source => (
                  <Option key={source} value={source}>{source}</Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={5}>
              <Select
                mode="multiple"
                placeholder="Filter by Event"
                value={filters.eventNames}
                onChange={handleEventNameChange}
                style={{ width: '100%' }}
                allowClear
              >
                {uniqueEventNames.map(eventName => (
                  <Option key={eventName} value={eventName}>{eventName}</Option>
                ))}
              </Select>
            </Col>
          </Row>
          <Row justify="end" gutter={[8, 8]} align="middle">
            <Col style={{ marginRight: 'auto' }}>
              <Space
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => setFilters(prev => ({ ...prev, showNotifications: !prev.showNotifications }))}
              >
                <Switch
                  size="small"
                  checked={filters.showNotifications}
                />
                <span>Show Notifications Sent</span>
              </Space>
            </Col>
            <Col>
              <Button
                onClick={handleClearFilters}
                className="clearBtn"
                style={{ width: 'auto', paddingLeft: 16, paddingRight: 16 }}
                icon={<CloseCircleOutlined />}
              >
                Clear Filters
              </Button>
            </Col>
            <Col>
              <Button
                onClick={handleExportCSV}
                type="primary"
                icon={<DownloadOutlined />}
                disabled={filteredActivities.length === 0}
              >
                Export CSV
              </Button>
            </Col>
            {/* Internal Export button - temporarily always visible for testing */}
            <Col>
              <Button
                onClick={handleInternalExport}
                type="default"
                icon={<DownloadOutlined />}
                disabled={!props.activitylogs || props.activitylogs.length === 0}
              >
                Internal Export
              </Button>
            </Col>
            {/* DEBUG button commented out as requested */}
            {/* <Col>
              <Button
                onClick={() => setDebugMode(!debugMode)}
                type={debugMode ? "primary" : "default"}
              >
                {debugMode ? "Hide Debug" : "Show Debug"}
              </Button>
            </Col> */}
            {debugMode && debugOutput && (
              <Col>
                <Button
                  onClick={handleCopyDebug}
                  icon={<DownloadOutlined />}
                >
                  Copy Debug
                </Button>
              </Col>
            )}
          </Row>
        </div>
        <Spin spinning={props.isLoading || isEnhancing}>
          <Row style={{ overflow: 'auto' }}>
            <Table
              className='locate-patient-table'
              columns={columns}
              style={{ width: '100%' }}
              dataSource={filteredActivities}
              pagination={{ 
                defaultPageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: ['10', '25', '50', '100', '500'],
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} activities`,
              }}
              scroll={{ x: 800 }}
            />
          </Row>
        </Spin>
        
        {debugMode && debugOutput && (
          <Card 
            title="Debug Information" 
            style={{ marginTop: 16 }}
            size="small"
          >
            <pre 
              style={{ 
                backgroundColor: '#f5f5f5',
                padding: '12px',
                borderRadius: '4px',
                fontSize: '12px',
                whiteSpace: 'pre-wrap',
                maxHeight: '400px',
                overflow: 'auto'
              }}
            >
              {debugOutput}
            </pre>
          </Card>
        )}
      </Card>

      <Modal
        title={contentModalTitle}
        open={contentModalVisible}
        onCancel={() => {
          setContentModalVisible(false)
          setContentModalHtml([])
        }}
        footer={null}
        width={800}
        bodyStyle={{ padding: 0 }}
      >
        <Spin spinning={contentLoading}>
          {contentModalHtml.length === 1 ? (
            <iframe
              srcDoc={contentModalHtml[0].content}
              style={{ width: '100%', height: '500px', border: 'none' }}
              title={contentModalHtml[0].title}
            />
          ) : contentModalHtml.length > 1 ? (
            <Tabs
              items={contentModalHtml.map((item, index) => ({
                key: String(index),
                label: item.title,
                children: (
                  <iframe
                    srcDoc={item.content}
                    style={{ width: '100%', height: '500px', border: 'none' }}
                    title={item.title}
                  />
                ),
              }))}
            />
          ) : null}
        </Spin>
      </Modal>
    </>
  )
}

const mapStateToProps = (state) => {
  return {
    isLoading: state.profile.isLoading,
    patient: state.patient.patient,
    activitylogs: state.patient.activitylogs,
    // Added for Internal Export functionality
    user: state.profile.user,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    getActivityLog: (input) => dispatch(getActivityLog(input)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ActivityLog)
